/**
 * Festival mode — on a phone, MyRave is the festival you are at (plan.md step 59).
 *
 * **Which festival is decided on the device**, from what the device already
 * knows: the raves this person attends, the raves stored for offline, the one
 * opened last, today against each edition's dates — and, only if they turned
 * it on, their distance to the site. **Never GPS alone**: a place narrows a
 * choice between raves the person has some tie to; it never picks a rave by
 * itself. And nothing here is ever sent anywhere — this is a pure function on
 * the phone, which is what keeps the privacy policy's sentence about location
 * true.
 *
 * Pure: the web app, the installed PWA and the phone's web view all run it.
 */
import { readNativeApp } from './release.js';
import { haversineMetres } from './geo.js';

export interface FestivalCandidate {
  editionId: string;
  subdomain: string;
  editionSlug: string;
  name: string;
  gatesOpenAt: Date | null;
  endsAt: Date | null;
  lat?: number | null;
  lng?: number | null;
  /** In `/me/festivals`. */
  attending: boolean;
  /** Its bundle is stored on this device. */
  stored: boolean;
  /** The rave opened last on this device. */
  lastOpened: boolean;
}

export type FestivalPick =
  | { kind: 'one'; candidate: FestivalCandidate; reason: string }
  | { kind: 'choose'; candidates: FestivalCandidate[] }
  | { kind: 'none' };

/** "At the festival" starts a day before the gates and lasts until six hours after the end. */
export const AROUND_BEFORE_MS = 24 * 3_600_000;
export const AROUND_AFTER_MS = 6 * 3_600_000;
/** Within this of the site counts as there. Big sites are kilometres across. */
export const NEAR_SITE_METRES = 5_000;

export function isOnNow(c: Pick<FestivalCandidate, 'gatesOpenAt' | 'endsAt'>, now: Date): boolean {
  if (!c.gatesOpenAt || !c.endsAt) return false;
  return now.getTime() >= c.gatesOpenAt.getTime() - AROUND_BEFORE_MS
    && now.getTime() <= c.endsAt.getTime() + AROUND_AFTER_MS;
}

/**
 * One clear match opens it; two ask; none goes to My raves.
 *
 * A candidate qualifies when it is on NOW and the person has a tie to it
 * (attending, stored, opened last). Among several, attending beats the rest;
 * then, if a place was given, the ones near it.
 */
export function pickFestival(
  all: readonly FestivalCandidate[], now: Date, here: { lat: number; lng: number } | null = null,
): FestivalPick {
  const tied = all.filter((c) => isOnNow(c, now) && (c.attending || c.stored || c.lastOpened));
  if (tied.length === 0) return { kind: 'none' };
  if (tied.length === 1) return { kind: 'one', candidate: tied[0]!, reason: reasonFor(tied[0]!) };

  const attending = tied.filter((c) => c.attending);
  let narrowed = attending.length > 0 ? attending : tied;
  if (here && narrowed.length > 1) {
    const near = narrowed.filter((c) => c.lat != null && c.lng != null
      && haversineMetres(here.lat, here.lng, c.lat, c.lng) <= NEAR_SITE_METRES);
    if (near.length > 0) narrowed = near;
  }
  if (narrowed.length === 1) return { kind: 'one', candidate: narrowed[0]!, reason: here ? 'near' : reasonFor(narrowed[0]!) };
  return { kind: 'choose', candidates: narrowed };
}

function reasonFor(c: FestivalCandidate): string {
  return c.attending ? 'attending' : c.stored ? 'stored' : 'last-opened';
}

/** Festival mode is for the phone: the MyRave app's web view, or the web app installed to a home screen. */
export function isFestivalModeClient(userAgent: string, standalone: boolean): boolean {
  return readNativeApp(userAgent) !== null || standalone;
}
